import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useRemediation } from '../../context/RemediationContext';

const { FiBell, FiAlertTriangle, FiUser, FiShield, FiCheck, FiChevronDown, FiChevronUp } = FiIcons;

const typeConfig = {
  learner: { icon: FiUser, color: 'text-blue-600 bg-blue-50 border-blue-100', label: 'Learner' },
  manager: { icon: FiAlertTriangle, color: 'text-orange-600 bg-orange-50 border-orange-100', label: 'Manager' },
  admin: { icon: FiShield, color: 'text-red-600 bg-red-50 border-red-100', label: 'Admin' },
};

const NotificationsPanel = () => {
  const { notifications, markNotificationRead } = useRemediation();
  const [expanded, setExpanded] = useState(true);
  const unread = notifications.filter(n => !n.read).length;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <button onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between px-5 py-4 border-b border-slate-100 hover:bg-slate-50 transition-colors">
        <div className="flex items-center gap-2">
          <SafeIcon icon={FiBell} className="text-violet-500" />
          <span className="text-sm font-semibold text-slate-700">Notifications</span>
          {unread > 0 && (
            <span className="bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">{unread}</span>
          )}
        </div>
        <SafeIcon icon={expanded ? FiChevronUp : FiChevronDown} className="text-slate-400" />
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
            <div className="p-3 space-y-2 max-h-72 overflow-y-auto">
              {notifications.length === 0 && (
                <p className="text-xs text-slate-400 text-center py-4">No notifications yet.</p>
              )}
              {notifications.map(n => {
                const cfg = typeConfig[n.type] || typeConfig.learner;
                return (
                  <motion.div key={n.id} layout initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }}
                    className={`flex items-start gap-2.5 p-3 rounded-lg border ${cfg.color} ${n.read ? 'opacity-60' : ''}`}>
                    <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 bg-white/60">
                      <SafeIcon icon={cfg.icon} className="text-xs" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-slate-700 capitalize">{cfg.label} Alert</p>
                      <p className="text-xs text-slate-600 leading-snug mt-0.5">{n.message}</p>
                      <p className="text-xs text-slate-400 mt-1">{new Date(n.timestamp).toLocaleTimeString()}</p>
                    </div>
                    {!n.read && (
                      <button onClick={() => markNotificationRead(n.id)} className="text-slate-300 hover:text-emerald-500 flex-shrink-0 transition-colors">
                        <SafeIcon icon={FiCheck} className="text-xs" />
                      </button>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationsPanel;